import { NumberInput, Tooltip } from "@mantine/core";
import { t } from "i18next";
import type {
  MRT_ColumnDef,
  MRT_TableInstance
} from "mantine-react-table-open";
import { useEffect, useState } from "react";
import { setErrorsFromResponseData } from "../../../../components/BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { MAX_GENERATOR_WEIGHTING_PER_LOCATION } from "../../../../components/StructureView/StructureView.constants";
import { HeaderWithHelpIcon } from "../../../../mantine/mrt/components/header/HeaderWithHelpIcon";
import { useToast } from "../../../../mantine/notifications/useToast";
import type {
  ExpressOnboardingGenerator,
  GetErrorFn,
  SetErrorFn
} from "../../ExpressOnboarding.types";
import { useExpressOnboardingMutations } from "../hooks/useExpressOnboardingMutations";

function getExpressOnboardingTableGenerationShareColumn(
  getError: GetErrorFn,
  setError: SetErrorFn
): MRT_ColumnDef<ExpressOnboardingGenerator> {
  return {
    id: "generation_share",
    accessorKey: "generation_share",
    header: "Aufteilungsfaktor",
    Header: () => (
      <HeaderWithHelpIcon
        headerText="Aufteilungsfaktor"
        helpText={`Der Aufteilungsfaktor muss zwischen 0 und 1 liegen.\n\nAndere Eingaben werden automatisch korrigiert.`}
      />
    ),
    enableGlobalFilter: false,
    Cell: ({ cell, row, table }) => {
      const value =
        cell.getValue<ExpressOnboardingGenerator["generation_share"]>();

      const hasLocationId =
        !!row.getValue<ExpressOnboardingGenerator["location_id"]>(
          "location_id"
        );

      /* local state is required for refreshes by other cells (location_id) */
      const [currentValue, setCurrentValue] =
        useState<ExpressOnboardingGenerator["generation_share"]>(value);

      useEffect(() => {
        setCurrentValue(value);
      }, [value]);

      const { expressOnboardingGeneratorSave, pendingGeneratorIds } =
        useExpressOnboardingMutations();

      const { showErrorToast } = useToast();

      const rowIsSaving = pendingGeneratorIds.has(row.original.id);

      return (
        <Tooltip
          disabled={hasLocationId}
          label="Ein Aufteilungsfaktor kann nur für Zählpunkte mit Standort hinterlegt werden."
        >
          <NumberInput
            disabled={!hasLocationId || rowIsSaving}
            error={getError(row.original.id, "generation_share")}
            loading={rowIsSaving}
            max={1}
            min={0}
            value={currentValue ?? ""}
            onBlur={() => {
              const hasChange = currentValue !== value;
              if (!hasChange) {
                return;
              }

              const validation = validateGenerationShare({
                table: table,
                siteId: row.original.site_id,
                locationId: row.original.location_id,
                generatorId: row.original.id,
                currentValue: currentValue
              });

              setError(row.original.id, {
                generation_share:
                  typeof validation === "string" ? validation : undefined
              });

              if (typeof validation === "string") {
                return;
              }

              expressOnboardingGeneratorSave
                .mutateAsync({
                  id: row.original.id,
                  generation_share: currentValue
                })
                .catch((error) => {
                  setErrorsFromResponseData(
                    showErrorToast,
                    error,
                    row.original,
                    (field: string, err: { message?: string }) =>
                      setError(row.original.id, {
                        [field]: err?.message ?? t("errors.UnknownError")
                      }),
                    t("errors.UnknownError")
                  );
                });
            }}
            onChange={(newValue) => {
              setCurrentValue(
                newValue === ""
                  ? null
                  : typeof newValue === "number"
                    ? newValue
                    : parseFloat(newValue)
              );
            }}
          />
        </Tooltip>
      );
    }
  };
}

interface ValidateGenerationShareParams {
  table: MRT_TableInstance<ExpressOnboardingGenerator>;
  siteId: ExpressOnboardingGenerator["site_id"];
  locationId: ExpressOnboardingGenerator["location_id"];
  generatorId: ExpressOnboardingGenerator["id"];
  currentValue: ExpressOnboardingGenerator["generation_share"];
}

function validateGenerationShare({
  table,
  siteId,
  locationId,
  generatorId,
  currentValue
}: ValidateGenerationShareParams): true | string {
  const siteSum = table
    .getRowModel()
    .rows.filter(
      (row) =>
        row.original.id !== generatorId &&
        row.original.site_id === siteId &&
        !!row.original.location_id &&
        row.original.location_id === locationId
    )
    .reduce(
      (sum, row) => sum + (row.original.generation_share ?? 0),
      currentValue ?? 0
    );

  const generationShareError = siteSum > MAX_GENERATOR_WEIGHTING_PER_LOCATION;

  if (generationShareError) {
    return t("errors.ExcessWeightingError", {
      maxWeighting: MAX_GENERATOR_WEIGHTING_PER_LOCATION
    });
  }
  return true;
}

export { getExpressOnboardingTableGenerationShareColumn };
